/**
 * Visibility-aware polling for hosted surfaces.
 *
 * The Studio badge is the only thing in the hosted app that polls, and a hidden
 * tab has nobody to show a heartbeat to, so polling it only spends Worker
 * requests. Returning to the tab refreshes immediately instead of waiting out
 * the remaining interval, which is what keeps the badge from reading stale on
 * the frame the user comes back.
 *
 * The scheduler and visibility source are injected so the behavior can be
 * asserted without a DOM or a real clock.
 */

export interface VisibilityPollingHost {
  readonly isHidden: () => boolean;
  readonly setInterval: (handler: () => void, ms: number) => number;
  readonly clearInterval: (handle: number) => void;
  readonly addVisibilityListener: (listener: () => void) => void;
  readonly removeVisibilityListener: (listener: () => void) => void;
}

export function browserVisibilityPollingHost(): VisibilityPollingHost {
  return {
    isHidden: () => document.hidden,
    setInterval: (handler, ms) => window.setInterval(handler, ms),
    clearInterval: (handle) => window.clearInterval(handle),
    addVisibilityListener: (listener) => document.addEventListener("visibilitychange", listener),
    removeVisibilityListener: (listener) =>
      document.removeEventListener("visibilitychange", listener),
  };
}

/**
 * Polls `poll` every `intervalMs` while the page is visible. Returns a stop
 * function that removes the listener and cancels any pending interval.
 *
 * Does not perform an initial call: callers already load once on mount, and
 * doing it here too would double every mount's first request.
 */
export function startVisibilityAwarePolling(input: {
  readonly poll: () => void;
  readonly intervalMs: number;
  readonly host: VisibilityPollingHost;
}): () => void {
  const { host, intervalMs, poll } = input;
  let handle: number | null = null;

  const stopInterval = () => {
    if (handle !== null) {
      host.clearInterval(handle);
      handle = null;
    }
  };

  const startInterval = () => {
    if (handle === null) {
      handle = host.setInterval(poll, intervalMs);
    }
  };

  const onVisibilityChange = () => {
    if (host.isHidden()) {
      stopInterval();
      return;
    }
    // Only a genuine hidden-to-visible transition refreshes. A visibility event
    // fired while already polling must not add an extra request.
    if (handle === null) {
      poll();
      startInterval();
    }
  };

  if (!host.isHidden()) {
    startInterval();
  }
  host.addVisibilityListener(onVisibilityChange);

  return () => {
    host.removeVisibilityListener(onVisibilityChange);
    stopInterval();
  };
}
